import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useCrewSite, useOfflineQueue, usePrepPack, useStartSiteInspection, useUploadCrewPhoto, type CrewSite } from "../../api/crew";
import { StatusChip } from "../../components/ui";
import { directionsUrl, distanceKm, formatKm } from "./crewDay";
import { PendingBadge } from "./PendingBadge";
import PrepPackView from "./PrepPackView";
import { SiteStatusCard } from "./SiteStatusCard";
import "../../styles/crew.css";

/** One of the field operators' sites, in the order it is used at the kerb:
 * the way there, the way into the checklist, what to take (its own page),
 * then the site's papers and photos, and last where the work stands. A
 * status move that cannot be sent is queued, and says so. */
export default function CrewSitePage() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const siteId = Number(id);
  const site = useCrewSite(siteId);
  const queue = useOfflineQueue();
  if (site.isPending) {
    return <p className="crew-state">Loading the site…</p>;
  }
  if (site.isError) {
    return (
      <div className="crew-page">
        <Link to="/crew" className="crew-back">
          ‹ My work
        </Link>
        <p className="notice bad">This site could not be loaded: {site.error.message}</p>
      </div>
    );
  }
  if (pathname.endsWith("/prep")) {
    return <PrepPage site={site.data} pending={queue.pending} onFlush={() => void queue.flush()} />;
  }
  return <CrewSiteView site={site.data} pending={queue.pending} onFlush={() => void queue.flush()} />;
}

/** Where the phone is, asked once, if the browser will say. A refusal or no
 * fix leaves it unknown and the map box simply gives no distance. */
function useHere(wanted: boolean): { lat: number; lon: number } | null {
  const [here, setHere] = useState<{ lat: number; lon: number } | null>(null);
  useEffect(() => {
    if (!wanted || typeof navigator === "undefined" || !("geolocation" in navigator)) {
      return;
    }
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!cancelled) {
          setHere({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        }
      },
      () => undefined,
      { maximumAge: 120_000, timeout: 10_000 },
    );
    return () => {
      cancelled = true;
    };
  }, [wanted]);
  return here;
}

function MapBox({ site }: { site: CrewSite }) {
  const located = site.lat !== null && site.lon !== null;
  const here = useHere(located);
  if (site.lat === null || site.lon === null) {
    return <div className="dashed-box crew-mapbox">No position for this site yet. Ask the office to place it.</div>;
  }
  const away = here ? ` · ${formatKm(distanceKm(here, { lat: site.lat, lon: site.lon }))} away` : "";
  return (
    <a className="dashed-box crew-mapbox" href={directionsUrl(site.lat, site.lon)} target="_blank" rel="noreferrer">
      <span>Map{away}</span>
      <span className="crew-mapbox-go">Navigate ›</span>
      <span className="crew-note mono">
        {site.lat.toFixed(5)}, {site.lon.toFixed(5)}
      </span>
    </a>
  );
}

/** The way into the checklist: opens the draft that exists, or starts one. */
export function ChecklistButton({ site }: { site: CrewSite }) {
  const navigate = useNavigate();
  const start = useStartSiteInspection(site.id);
  if (site.open_inspection_id !== null) {
    return (
      <Link className="crew-btn crew-btn-go crew-btn-big" to={`/inspections/${site.open_inspection_id}`}>
        Continue the checklist
      </Link>
    );
  }
  if (!site.can_start_inspection) {
    return <p className="crew-note">This site has no notification number, so the office must raise the inspection.</p>;
  }
  return (
    <>
      <button
        type="button"
        className="crew-btn crew-btn-go crew-btn-big"
        disabled={start.isPending}
        onClick={() => start.mutate(undefined, { onSuccess: (r) => navigate(`/inspections/${r.inspection_id}`) })}
      >
        {start.isPending ? "Opening…" : "Start the checklist"}
      </button>
      {start.isError ? <p className="notice bad">{start.error.message}</p> : null}
    </>
  );
}

/** One line for the preparation pack, with how much of it is ticked; the
 * pack itself is its own page, read in the yard rather than at the kerb. */
export function PrepSummary({ site }: { site: CrewSite }) {
  const pack = usePrepPack(site.id);
  const lines = pack.data?.sections.flatMap((section) => section.lines) ?? [];
  const done = lines.filter((line) => line.done).length;
  const count = pack.data ? `${done} of ${lines.length} ticked` : pack.isError ? "could not be loaded" : "loading…";
  return (
    <Link className="crew-card crew-prep-summary" to={`/crew/sites/${site.id}/prep`} aria-label={`What to take, ${count}`}>
      <span className="crew-prep-summary-text">
        <strong>What to take</strong>
        <span className="crew-note">Preparation pack · {count}</span>
      </span>
      <span className="crew-prep-summary-go" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

function Photos({ site }: { site: CrewSite }) {
  const upload = useUploadCrewPhoto(site.id);
  const input = useRef<HTMLInputElement>(null);
  return (
    <section className="crew-card" aria-label="Photos">
      <h2 className="section-label">Photos · {site.photos.length}</h2>
      {site.photos.length > 0 ? (
        <div className="photo-grid">
          {site.photos.map((p) => (
            <figure key={p.id}>
              <a href={p.url} target="_blank" rel="noreferrer">
                <img src={p.url} alt={p.caption ?? "Site photo"} loading="lazy" />
              </a>
              {p.caption ? <figcaption>{p.caption}</figcaption> : null}
            </figure>
          ))}
        </div>
      ) : null}
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            upload.mutate(file);
          }
          e.target.value = "";
        }}
      />
      <button type="button" className="crew-btn crew-btn-outline" disabled={upload.isPending} onClick={() => input.current?.click()}>
        {upload.isPending ? "Uploading…" : "Take or add a photo"}
      </button>
      {upload.isError ? <p className="notice bad">{upload.error.message} Photos need a signal.</p> : null}
    </section>
  );
}

function Facts({ site }: { site: CrewSite }) {
  const valve = [site.valve_make, site.valve_desc, site.dn_scope ? `DN${site.dn_scope}` : null].filter(Boolean).join(" ");
  return (
    <section className="crew-card" aria-label="About this site">
      <h2 className="section-label">About this site</h2>
      <dl className="crew-facts">
        <dt>Address</dt>
        <dd>{site.address ?? "—"}</dd>
        <dt>Works order</dt>
        <dd className="mono">{site.notif_no ?? "—"}</dd>
        <dt>Valve</dt>
        <dd>{valve || "—"}</dd>
        <dt>Station</dt>
        <dd>{site.station_type ?? "—"}</dd>
      </dl>
      <div className="crew-docs">
        {site.qcp_url ? (
          <a className="crew-btn crew-btn-outline" href={site.qcp_url}>
            QCP for field operators (PDF)
          </a>
        ) : (
          <span className="crew-note">No field copy of the QCP.</span>
        )}
        <a className="crew-btn crew-btn-outline" href={site.prep_pdf_url}>
          Prep pack (PDF)
        </a>
      </div>
    </section>
  );
}

function SiteHead({ site, pending, onFlush, back }: { site: CrewSite; pending: number; onFlush: () => void; back: { to: string; label: string } }) {
  return (
    <header className="crew-head crew-site-head">
      <div className="crew-site-top">
        <Link to={back.to} className="crew-back">
          ‹ {back.label}
        </Link>
        <PendingBadge pending={pending} onFlush={onFlush} />
      </div>
      <h1>{site.name}</h1>
      <div className="crew-site-sub">
        <StatusChip tone={site.status === "completed" ? "ok" : "info"}>{site.status_label}</StatusChip>
        {site.notif_no || site.rs_no ? (
          <span className="crew-note">{[site.notif_no ? `Works order ${site.notif_no}` : null, site.rs_no].filter(Boolean).join(" · ")}</span>
        ) : null}
      </div>
    </header>
  );
}

export function CrewSiteView({ site, pending, onFlush }: { site: CrewSite; pending: number; onFlush: () => void }) {
  return (
    <div className="crew-page crew-site" aria-label={`Site ${site.name}`}>
      <SiteHead site={site} pending={pending} onFlush={onFlush} back={{ to: "/crew", label: "My work" }} />
      <div className="crew-site-body">
        <div className="crew-site-main">
          <MapBox site={site} />
          <ChecklistButton site={site} />
          <PrepSummary site={site} />
          <SiteStatusCard site={site} />
        </div>
        <div className="crew-site-side">
          <Facts site={site} />
          <Photos site={site} />
        </div>
      </div>
    </div>
  );
}

/** WHAT TO TAKE, on its own page: read in the yard before leaving, ticked
 * in the chamber. */
export function PrepPage({ site, pending, onFlush }: { site: CrewSite; pending: number; onFlush: () => void }) {
  return (
    <div className="crew-page crew-site" aria-label={`What to take for ${site.name}`}>
      <SiteHead site={site} pending={pending} onFlush={onFlush} back={{ to: `/crew/sites/${site.id}`, label: site.name }} />
      <PrepPackView siteId={site.id} />
    </div>
  );
}
